import { useMemo } from 'react';

import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import { useReferralBuildingTerminology } from 'hooks/useReferralBuildingTerminology';

type BuildingOption = { id: string; name: string };

// The facility set the inbound-referral surfaces (create + both edit screens) offer, scoped to the
// acting client's level of care (SNF, or home health). `facilities` is the raw scoped list (used
// where a building of that type is wanted, e.g. the provider-NPI prefill); `facilityOptions` is
// what the pickers show:
//
//   the scoped set ∪ the buildings already attached to this referral, deduped by id, sorted by name.
//
// The union keeps an already-attached building selectable even when it falls outside the scope
// (attached before this scope existed), so editing can't silently drop it and revoke its access.
// Sorting the merged list means an out-of-scope building lands by name rather than tacked on after
// the API's sorted set. Shared by ReferralModal and ReferralForm so the two edit surfaces
// can't drift on what's attachable to one referral.
export function useReferralFacilities(selectedBuildings: BuildingOption[]) {
  const { groupType } = useReferralBuildingTerminology();
  const { facilities, isPending } = useAllowedFacilities({ type: groupType });

  const facilityOptions = useMemo<BuildingOption[]>(() => {
    const byId = new Map<string, BuildingOption>();
    facilities.forEach((facility) => byId.set(facility.id, { id: facility.id, name: facility.name }));
    selectedBuildings.forEach((building) => {
      if (!byId.has(building.id)) byId.set(building.id, building);
    });
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [facilities, selectedBuildings]);

  return { facilities, facilityOptions, isPending };
}
